import { createGlobalStyle } from 'styled-components';

/**
 * Estilos globais — reset moderno + base tipográfica + carregamento de fontes.
 *
 * Reset baseado em práticas atuais (Josh W. Comeau / Andy Bell), adaptado.
 */
export const GlobalStyle = createGlobalStyle`
  /* ---------- Reset moderno ---------- */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  * {
    margin: 0;
    padding: 0;
  }

  html {
    -webkit-text-size-adjust: 100%;
    -moz-text-size-adjust: 100%;
    text-size-adjust: 100%;
    scroll-behavior: smooth;
    /* O Lenis vai assumir o controle do scroll — desabilita o nativo. */
    overflow-x: hidden;
  }

  body {
    min-height: 100dvh;
    font-family: ${({ theme }) => theme.fonts.body};
    font-size: ${({ theme }) => theme.fontSizes.base};
    font-weight: ${({ theme }) => theme.fontWeights.regular};
    line-height: ${({ theme }) => theme.lineHeights.normal};
    color: ${({ theme }) => theme.semantic.text.primary};
    background-color: ${({ theme }) => theme.semantic.background.primary};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    overflow-x: hidden;
  }

  /* Media e SVG responsivos por padrão. */
  img, picture, video, canvas, svg {
    display: block;
    max-width: 100%;
    height: auto;
  }

  /* Herdar tipografia em form elements. */
  input, button, textarea, select {
    font: inherit;
    color: inherit;
  }

  /* Quebra de palavras inteligente em textos. */
  p, h1, h2, h3, h4, h5, h6 {
    overflow-wrap: break-word;
  }

  /* Equilíbrio de quebra de linha em títulos (Chromium/Safari). */
  h1, h2, h3, h4, h5, h6 {
    /* stylelint-disable-next-line property-no-unknown */
    text-wrap: balance;
    font-family: ${({ theme }) => theme.fonts.display};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    line-height: ${({ theme }) => theme.lineHeights.tight};
    letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
  }

  /* Equilíbrio em parágrafos curtos (Chromium 114+). */
  p {
    /* stylelint-disable-next-line property-no-unknown */
    text-wrap: pretty;
  }

  /* Links acessíveis. */
  a {
    color: inherit;
    text-decoration: none;
  }

  /* Botões zerados — estilizados por componente. */
  button {
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
  }

  /* Listas zeradas — estilizadas por componente. */
  ul, ol {
    list-style: none;
  }

  /* Focus visível, acessível e bonito. */
  :focus-visible {
    outline: 2px solid ${({ theme }) => theme.semantic.brand.accent};
    outline-offset: 3px;
    border-radius: ${({ theme }) => theme.radii.xs};
  }

  /* Remove focus padrão para mouse. */
  :focus:not(:focus-visible) {
    outline: none;
  }

  /* Seleção de texto com a cor da marca. */
  ::selection {
    background-color: ${({ theme }) => theme.semantic.brand.accent};
    color: ${({ theme }) => theme.semantic.text.inverse};
  }

  /* Scrollbar customizada — sutil e elegante. */
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.semantic.background.secondary};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.navy[300]};
    border-radius: ${({ theme }) => theme.radii.full};
    border: 2px solid ${({ theme }) => theme.semantic.background.secondary};

    &:hover {
      background: ${({ theme }) => theme.colors.navy[500]};
    }
  }

  /* Firefox. */
  html {
    scrollbar-color: ${({ theme }) => theme.colors.navy[300]} ${({ theme }) => theme.semantic.background.secondary};
    scrollbar-width: thin;
  }

  /* Respeita usuários que pediram para reduzir movimento. */
  @media (prefers-reduced-motion: reduce) {
  *:not(.logo-carousel-track),
  *:not(.logo-carousel-track)::before,
  *:not(.logo-carousel-track)::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

  /* Lenis — estilos exigidos pela biblioteca. */
  html.lenis,
  html.lenis body {
    height: auto;
  }

  .lenis.lenis-smooth {
    scroll-behavior: auto !important;
  }

  .lenis.lenis-smooth [data-lenis-prevent] {
    overscroll-behavior: contain;
  }

  .lenis.lenis-stopped {
    overflow: clip;
  }

  .lenis.lenis-smooth iframe {
    pointer-events: none;
  }

  /* Utility para esconder visualmente, mantendo acessível a leitores de tela. */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;
